<template>
  <div class="i-hello">
    <iBackground :color="white" />

    <template v-if="reportStore.invalidData">
      <iUnavailableCard
        text="Please try again later. If the issue persists please contact us at support@jobtest.org"
        title="Report unavailable"
      />
    </template>

    <template v-else>
      <template v-if="data.lightBoxVideo">
        <iBaseLightBox @close="data.lightBoxVideo = ''">
          <video
            :src="data.lightBoxVideo"
            autoplay
            controls
            class="light-box-video"
            type="video/mp4"
          ></video>
        </iBaseLightBox>
      </template>

      <div class="page">
        <iReportCountdown
          ref="reportCountdown"
          version="start"
          has-fade-in-animation
          :timer-duration-min="data.timerDurationMin"
          @scroll-to-payment-table="scrollToSection('#payment-table')"
        />

        <iAnnouncementBar version="light" />

        <div class="content">
          <iIntroSection @scroll-to-snapshot="scrollToSection('#report-snapshot')" />
          <div
            id="report-snapshot"
            ref="snapshotSection"
          >
            <iReportSnapshot
              @scroll-to-personality="scrollToSection('#personality-section')"
              @scroll-to-careers="scrollToSection('#career-paths')"
              @scroll-to-earnings="scrollToSection('#earnings-section')"
            />
          </div>
          <iCoachSchoolDisplay @scroll-to-payment="scrollToSection('#payment-table')" />
          <div id="personality-section">
            <iPersonalitySection @scroll-to-payment="scrollToSection('#payment-table')" />
          </div>
          <iReviewSection />
          <div id="career-paths">
            <iEmergingPaths @scroll-to-payment="scrollToSection('#payment-table')" />
          </div>
          <div id="earnings-section">
            <iEarningPotential @scroll-to-payment="scrollToSection('#payment-table')" />
          </div>
          <iCareerRoadmap />
          <div id="payment-table">
            <iPaymentTable />
          </div>
          <iFooterSection />
        </div>
      </div>
    </template>
  </div>
</template>

<script setup lang="ts">
import { nextTick, onMounted, reactive, ref, type Ref } from "vue"
import { useReportStore } from "@/stores/report"

// STYLES
import { white } from "@/styles/colors"
import { breakpointLg } from "@/styles/variables"

// COMPONENTS
import iBaseLightBox from "@/components/base/BaseLightBox.vue"
import iBackground from "@/components/global/Background.vue"
import iUnavailableCard from "@/components/global/UnavailableCard.vue"
import iAnnouncementBar from "@/components/welcome/AnnouncementBar.vue"
import iReportCountdown from "@/components/hello/ReportCountdown.vue"
import iIntroSection from "@/components/hello/IntroSection.vue"
import iReportSnapshot from "@/components/hello/ReportSnapshot.vue"
import iPersonalitySection from "@/components/hello/PersonalitySection.vue"
import iReviewSection from "@/components/hello/ReviewSection.vue"
import iEmergingPaths from "@/components/hello/EmergingPaths.vue"
import iEarningPotential from "@/components/hello/EarningPotential.vue"
import iCareerRoadmap from "@/components/hello/CareerRoadmap.vue"
import iFooterSection from "@/components/hello/FooterSection.vue"
import iPaymentTable from "@/components/pricing/PaymentTableTest.vue"
import iCoachSchoolDisplay from "@/components/hello/CoachSchoolDisplay.vue"

// STORES
const reportStore = useReportStore()

// REFS
const reportCountdown: Ref<HTMLElement | null> = ref(null)
const snapshotSection: Ref<HTMLElement | null> = ref(null)

// DATA
const data = reactive({
  lightBoxVideo: "",
  timerDurationMin: 15,
  snapshotSectionDelayMs: 2000,
  zigpollModalDelayMs: 45000, // 45 seconds,
  reportCountdownOffset: "0px"
})

// FUNCTIONS
function loadZigpoll(delayMs: number) {
  const isMobileView = window.innerWidth <= breakpointLg

  if (!isMobileView) reportStore.initDesktopZigpollPrepayment(delayMs)
}

function scrollTo(element: Element | null) {
  if (!element) return

  // Calculate the target scroll position and scroll to
  const targetScrollPosition = element.getBoundingClientRect().top + window.scrollY - 16
  window.scrollTo({ top: targetScrollPosition, behavior: "smooth" })
}

function scrollToSection(elementId: string) {
  const element = document.querySelector(elementId)
  scrollTo(element)
}

function observeSnapshotSection(delayMs: number) {
  setTimeout(() => {
    if (!snapshotSection.value || !reportCountdown.value) return
    setupObserver(snapshotSection, () => {
      ;(reportCountdown.value as any)?.makeActive?.()
    })
  }, delayMs)
}

function setupObserver(elementRef: Ref<HTMLElement | null>, callback: () => void) {
  if (!elementRef.value) return
  const observer = new IntersectionObserver(
    (entries, observer) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          callback() // Execute the callback function
          onReportCountdownResize()
          observer.unobserve(entry.target) // Stop observing after the first trigger
        }
      })
    },
    {
      root: null, // observing changes in the viewport
      rootMargin: "0px",
      threshold: 0.1 // callback will be executed when 10% of the target is visible
    }
  )
  observer.observe(elementRef.value)
}

function onReportCountdownResize() {
  nextTick(() => {
    const reportCountdown = document.querySelector(".i-report-countdown") as HTMLElement

    if (!reportCountdown) return

    const observer = new ResizeObserver(() => {
      const offsetHeight = `${reportCountdown.getBoundingClientRect().height}px`
      if (data.reportCountdownOffset != offsetHeight) data.reportCountdownOffset = offsetHeight
    })
    observer.observe(reportCountdown)
  })
}

// LIFECYCLE HOOKS
onMounted(() => {
  observeSnapshotSection(data.snapshotSectionDelayMs)
  loadZigpoll(data.zigpollModalDelayMs)
})
</script>

<style scoped lang="scss">
.i-hello {
  .zigpoll-modal {
    visibility: hidden;

    &.visible {
      visibility: visible;
    }

    &-wrap {
      display: flex;
      flex-direction: column;
      min-height: 90px;
      max-height: 60vh;
      overflow: auto;
      position: relative;
    }

    &-text {
      font-weight: bold;
      position: absolute;
      text-align: center;
      top: 50%;
      width: 100%;
    }
  }

  .page {
    @include flex(column, center, stretch);

    overflow-x: clip;
    background-color: $black50;

    .content {
      @include flex($direction: column, $justify: start, $align: stretch, $wrap: nowrap, $gap: 0);

      width: 100%;
      padding-top: 24px;
    }
  }

  .modal {
    .bottom {
      @include flex($direction: row, $justify: center, $align: center, $gap: 0);

      background-color: $black50;
      padding: 12px 25px;
      border-bottom-left-radius: 16px;
      border-bottom-right-radius: 16px;
    }
  }
}
</style>
